'use client';

import { useEffect, useRef } from 'react';

import { useParams } from 'next/navigation';

import { useRouter, usePathname } from '@/i18n/navigation';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import {
  C_SESSION_STORAGE_KEY,
  C_SUPPORTED_LOCALES
} from '@/ts/constants/C_LanguageSync';
import { E_Locales } from '@/ts/enums/E_Locales';
import { T_LanguageSyncReturn } from '@/ts/types/T_LanguageSync';

function isRewardSubdomain(): boolean {
  if (typeof window === 'undefined') return false;
  const subdomainName = (
    process.env.NEXT_PUBLIC_SUBDOMAIN_NAME || 'reward'
  ).toLowerCase();
  const hostname = window.location.hostname.toLowerCase();
  return subdomainName.length > 0 && hostname.startsWith(`${subdomainName}.`);
}

export function useLanguageSync(): T_LanguageSyncReturn {
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams();
  const user = useGlobalData('user');
  const hasRedirected = useRef(false);

  // Check if user manually changed language during current session
  const checkManualLanguageChange = (): boolean => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(C_SESSION_STORAGE_KEY) === 'true';
  };

  // Set manual language change flag in session storage
  const setManualLanguageChange = (value: boolean): void => {
    if (typeof window === 'undefined') return;
    if (value) {
      sessionStorage.setItem(C_SESSION_STORAGE_KEY, 'true');
    } else {
      sessionStorage.removeItem(C_SESSION_STORAGE_KEY);
    }
  };

  // Sync locale with user's saved language preference
  // On reward subdomain: always respect URL locale (from routing) - never override with user preference.
  // This ensures when redirecting from domain with /en to reward subdomain, we show /en, not user's saved fr.
  useEffect(() => {
    if (isRewardSubdomain()) return;
    if (!user?.language || hasRedirected.current) return;
    if (checkManualLanguageChange()) return;

    const currentLocale = (params.locale as E_Locales) || E_Locales.EN;
    const userLanguage = user.language as E_Locales;

    if (!C_SUPPORTED_LOCALES.includes(userLanguage)) return;
    if (currentLocale === userLanguage) return;

    // Soft navigation to change locale without full page refresh.
    // The hasRedirected ref prevents re-triggering during the transition.
    // NextIntlProvider callbacks are memoized to prevent cascading re-renders.
    hasRedirected.current = true;
    router.replace(pathname, { locale: userLanguage });
  }, [user?.language, pathname, router, params.locale]);

  // Reset flags when user logs out
  useEffect(() => {
    if (!user?.uuid) {
      hasRedirected.current = false;
      setManualLanguageChange(false);
    }
  }, [user?.uuid]);

  // Return utility functions for external use
  return {
    setManualLanguageChange,
    checkManualLanguageChange
  };
}
